import {getSituacaoOcorrencia} from "./situacaoOcorrencia";
export const GRUPOS_FOLHA = [
    {tipo: 1, chave: "ATIVOS", rotulo: "Ativos"},
    {tipo: 11, chave: "INATIVOS", rotulo: "Inativos"},
    {tipo: 21, chave: "PENSIONISTAS", rotulo: "Pensionistas"}
];

function centavos(valor) {
    let texto = String(valor ?? "").trim();
    if (texto.includes(",")) texto = texto.replace(/\./g, "").replace(",", ".");
    if (!/^-?\d+(\.\d{1,2})?$/.test(texto)) {
        throw new Error("A folha contém um valor inválido para conferência.");
    }
    const negativo = texto.startsWith("-");
    const [inteiro, fracao = ""] = texto.replace(/^-/, "").split(".");
    const resultado = Number(inteiro) * 100 + Number(fracao.padEnd(2, "0"));
    if (!Number.isSafeInteger(resultado))
        throw new Error("Valor da folha fora do limite de conferência.");
    return negativo ? -resultado : resultado;
}

// Mantém apenas o resumo financeiro no cache, sem CPF ou conta bancária.
export function resumirOcorrenciaFolha(ocorrencia) {
    const payload =
        typeof ocorrencia.dadosEnviados === "string"
            ? JSON.parse(ocorrencia.dadosEnviados)
            : ocorrencia.dadosEnviados;
    const parametros = payload?.parametros;
    const grupo = GRUPOS_FOLHA.find(
        (item) =>
            item.chave ===
            {
                1: "ATIVOS",
                2: "ATIVOS",
                3: "ATIVOS",
                4: "ATIVOS",
                5: "ATIVOS",
                11: "INATIVOS",
                12: "INATIVOS",
                14: "INATIVOS",
                21: "PENSIONISTAS",
                22: "PENSIONISTAS",
                24: "PENSIONISTAS"
            }[Number(parametros?.tipoFolha)]
    );
    if (
        ocorrencia.eventoAtual !== "FOLHA_PAGAMENTO_INICIAR" ||
        ocorrencia.ocorrenciaPaiId ||
        ocorrencia.cancelamentoConcluido ||
        !grupo
    )
        return null;
    if (
        !Array.isArray(payload?.servidores) ||
        !/^\d{6}$/.test(String(parametros.anoMes))
    ) {
        throw new Error(
            "A folha não contém os dados necessários para conferência."
        );
    }
    let proventosCentavos = 0;
    let descontosCentavos = 0;
    let lancamentos = 0;
    payload.servidores.forEach((servidor) => {
        if (!Array.isArray(servidor.lancamentos))
            throw new Error("Servidor sem lançamentos para conferência.");
        servidor.lancamentos.forEach((item) => {
            const valor = centavos(item.valorComponente);
            if (item.indicativoProventoDesconto === "P")
                proventosCentavos += valor;
            else if (item.indicativoProventoDesconto === "D")
                descontosCentavos += valor;
            else
                throw new Error(
                    "Indicativo de provento/desconto inválido na folha."
                );
            lancamentos++;
        });
    });
    const concluida =
        ocorrencia.estadoEnvio === "PROCESSADO_COM_SUCESSO" &&
        (ocorrencia.destinoEntrega === "FORNECEDOR"
            ? (ocorrencia.situacaoFornecedor
                  ? ocorrencia.situacaoFornecedor.aceiteTceMsConfirmado === true
                  : ocorrencia.resposta?.aceiteTceMs === "CONFIRMADO") &&
              Boolean(ocorrencia.codigoRegistroInformacao) &&
              ocorrencia.codigoRegistroInformacao ===
                  ocorrencia.resposta?.codigoRegistroInformacao &&
              Boolean(ocorrencia.resposta?.protocoloFornecedor)
            : (ocorrencia.ocorrenciasFilhas || []).some(
                  (etapa) =>
                      etapa.eventoAtual === "FOLHA_PAGAMENTO_FINALIZAR" &&
                      etapa.estadoEnvio === "PROCESSADO_COM_SUCESSO"
              ));
    return {
        id: ocorrencia.id,
        competencia: String(parametros.anoMes),
        tipo: Number(parametros.tipoFolha),
        categoriaOrigem:
            payload.categoriaFolhaOrigem ||
            (Number(parametros.tipoFolha) === 3
                ? "FERIAS"
                : Number(parametros.tipoFolha) === 5
                ? "RESCISAO"
                : [1, 11, 21].includes(Number(parametros.tipoFolha))
                ? "NORMAL"
                : [2, 12, 22].includes(Number(parametros.tipoFolha))
                ? "DECIMO_TERCEIRO"
                : "SUPLEMENTAR"),
        grupo: grupo.chave,
        sequencia: parametros.seqFolhaPagamento,
        arquivada: ocorrencia.arquivado === "S",
        estado: ocorrencia.estadoEnvio,
        concluida,
        situacao: getSituacaoOcorrencia(ocorrencia),
        servidores: payload.servidores.length,
        lancamentos,
        proventosCentavos,
        descontosCentavos,
        destino: ocorrencia.destinoEntrega || "NAO_CLASSIFICADO"
    };
}

export function consolidarFolhaCompetencia(
    folhas,
    competencia,
    categoria = "NORMAL"
) {
    // Arquivar não cancela um aceite oficial. Retiradas locais sem aceite
    // saem do total; cancelamentos oficiais já foram excluídos no resumo.
    const vigentes = folhas.filter(
        (item) =>
            item.competencia === competencia &&
            (!item.arquivada || item.concluida)
    );
    const porChave = new Map();
    vigentes.forEach((item) => {
        const chave = [item.competencia, item.tipo, item.sequencia].join("|");
        const remessas = porChave.get(chave) || [];
        if (!remessas.some((anterior) => anterior.id === item.id))
            remessas.push(item);
        porChave.set(chave, remessas);
    });
    const atuais = [...porChave.values()]
        .flatMap((remessas) =>
            remessas.map((item) => ({
                ...item,
                conflito:
                    remessas.length > 1 ||
                    !Number.isInteger(Number(item.sequencia)) ||
                    Number(item.sequencia) < 1
            }))
        )
        .filter(
            (item) =>
                categoria === "TODAS" ||
                (item.categoriaOrigem || "NORMAL") === categoria
        );
    return GRUPOS_FOLHA.map((grupo) => {
        const remessas = atuais.filter(
            (item) =>
                item.grupo === grupo.chave ||
                (!item.grupo && item.tipo === grupo.tipo)
        );
        const conflito = remessas.some((item) => item.conflito);
        return {
            ...grupo,
            conflito,
            remessas,
            preparada: remessas.length > 0,
            servidores: conflito
                ? null
                : remessas.reduce((total, item) => total + item.servidores, 0),
            lancamentos: conflito
                ? null
                : remessas.reduce((total, item) => total + item.lancamentos, 0),
            proventosCentavos: conflito
                ? null
                : remessas.reduce(
                      (total, item) => total + item.proventosCentavos,
                      0
                  ),
            descontosCentavos: conflito
                ? null
                : remessas.reduce(
                      (total, item) => total + item.descontosCentavos,
                      0
                  )
        };
    });
}
